import React, { useEffect } from "react";
import LegalLayout from "@/components/LegalLayout";

// Every answer below is drawn from copy already published on this website
// (The Revive Method, Medical Disclaimer, Terms of Use, booking section).
// Answers 4 and 5 are general patient guidance pending physician review.
const CONTENT = {
  en: {
    title: "Frequently Asked Questions",
    lastUpdated: "July 29, 2026",
    description: "Answers about regenerative medicine at REVIVE in Tijuana: how medical history guides treatment, suitability assessment, evaluation preparation, and how to request an appointment.",
    seoPath: "/faq",
    altPath: "/es/preguntas-frecuentes",
    intro: [
      "General answers about REVIVE Regenerative Medical Clinic and our approach to care. This information is educational and is not a substitute for individualized medical evaluation, diagnosis, or treatment.",
    ],
    faqs: [
      {
        q: "What is regenerative medicine?",
        a: "Regenerative medicine is an evolving area of medicine and biomedical research. The scientific evidence, regulatory status, recognized indications, risks, and potential benefits associated with particular regenerative treatments may vary depending on the specific product, procedure, medical condition, jurisdiction, and individual patient. At REVIVE, some therapies discussed — such as mesenchymal stem cell approaches — are areas of emerging research rather than established standard-of-care treatments, and no specific clinical outcome is guaranteed.",
      },
      {
        q: "How does REVIVE use medical history to guide treatment decisions?",
        a: "REVIVE combines each patient's medical history, physician assessment, and advances in regenerative science to guide individualized treatment decisions. The process begins with a consultation to understand the patient's medical history, concerns, and goals, followed by an evaluation that reviews appropriate laboratory testing, imaging, medical history, and previous treatments. The medical team then develops an individualized treatment strategy.",
      },
      {
        q: "How is treatment suitability assessed?",
        a: "Whether any treatment is appropriate for a particular patient depends on individual circumstances, medical history, examination, diagnostic information, contraindications, potential risks, and professional evaluation. Treatment recommendations and eligibility are determined only following appropriate medical evaluation and are subject to applicable laws, regulations, professional standards, product availability, and clinic protocols. Information on this website does not mean that every treatment is appropriate for or available to every patient.",
      },
      {
        q: "What should patients bring to their evaluation?",
        a: "Patients are generally asked to provide relevant medical records, recent laboratory or imaging results, and a list of current medications, so the medical team can review their history thoroughly. The exact documentation required may vary by case and is confirmed when the consultation is scheduled.",
      },
      {
        q: "What questions should patients ask about evidence, risks, alternatives, and follow-up?",
        a: "Before consenting to any treatment, patients are encouraged to ask: what clinical evidence supports this therapy for my condition, and is it established or emerging research? What are the potential risks and side effects? What alternatives exist, including not pursuing this treatment? What follow-up, monitoring, and supportive care will be provided? REVIVE's process includes follow-up to monitor progress and adjust supportive strategies when medically appropriate.",
      },
      {
        q: "How can someone request an appointment?",
        a: "You can request an in-person or virtual consultation through the Book a Consultation form on this website, send a message through the Contact Us page, or reach the patient care team directly on WhatsApp at +1 (555) 303-9140. Submitting a request does not guarantee an appointment; an appointment is considered scheduled only after confirmation from REVIVE. The clinic is located at Central Torreo, Av. Santa María, Calete, Int. 402, Tijuana, Baja California, Mexico, and is open Monday–Saturday, 9:00 AM – 5:00 PM.",
      },
    ],
  },
  es: {
    title: "Preguntas Frecuentes",
    lastUpdated: "29 de julio de 2026",
    description: "Respuestas sobre medicina regenerativa en REVIVE en Tijuana: cómo el historial médico guía el tratamiento, la evaluación de idoneidad, la preparación para la consulta y cómo solicitar una cita.",
    seoPath: "/es/preguntas-frecuentes",
    altPath: "/faq",
    intro: [
      "Respuestas generales sobre REVIVE Regenerative Medical Clinic y nuestro enfoque de atención. Esta información es educativa y no sustituye una evaluación, diagnóstico o tratamiento médico individualizado.",
    ],
    faqs: [
      {
        q: "¿Qué es la medicina regenerativa?",
        a: "La medicina regenerativa es un área en evolución de la medicina y la investigación biomédica. La evidencia científica, el estatus regulatorio, las indicaciones reconocidas, los riesgos y los beneficios potenciales asociados con tratamientos regenerativos particulares pueden variar según el producto, el procedimiento, la condición médica, la jurisdicción y el paciente individual. En REVIVE, algunas terapias mencionadas — como los enfoques con células madre mesenquimales — son áreas de investigación emergente y no tratamientos estándar establecidos, y no se garantiza ningún resultado clínico específico.",
      },
      {
        q: "¿Cómo utiliza REVIVE el historial médico para guiar las decisiones de tratamiento?",
        a: "REVIVE combina el historial médico de cada paciente, la valoración del médico y los avances de la ciencia regenerativa para guiar decisiones de tratamiento individualizadas. El proceso comienza con una consulta para comprender el historial médico, las inquietudes y los objetivos del paciente, seguida de una evaluación que revisa las pruebas de laboratorio, los estudios de imagen, el historial médico y los tratamientos previos pertinentes. A partir de ello, el equipo médico desarrolla una estrategia de tratamiento individualizada.",
      },
      {
        q: "¿Cómo se evalúa la idoneidad de un tratamiento?",
        a: "La idoneidad de cualquier tratamiento para un paciente en particular depende de las circunstancias individuales, el historial médico, la exploración, la información diagnóstica, las contraindicaciones, los riesgos potenciales y la evaluación profesional. Las recomendaciones de tratamiento y la elegibilidad se determinan únicamente tras una evaluación médica apropiada y están sujetas a las leyes, regulaciones, estándares profesionales, disponibilidad de productos y protocolos clínicos aplicables. La información de este sitio web no significa que cada tratamiento sea apropiado o esté disponible para cada paciente.",
      },
      {
        q: "¿Qué deben llevar los pacientes a su evaluación?",
        a: "Por lo general, se pide a los pacientes que proporcionen sus expedientes médicos relevantes, resultados recientes de laboratorio o estudios de imagen, y una lista de sus medicamentos actuales, para que el equipo médico pueda revisar a fondo su historial. La documentación exacta requerida puede variar según el caso y se confirma al programar la consulta.",
      },
      {
        q: "¿Qué preguntas deben hacer los pacientes sobre evidencia, riesgos, alternativas y seguimiento?",
        a: "Antes de dar su consentimiento para cualquier tratamiento, recomendamos a los pacientes preguntar: ¿qué evidencia clínica respalda esta terapia para mi condición, y se trata de evidencia establecida o de investigación emergente? ¿Cuáles son los riesgos y efectos secundarios potenciales? ¿Qué alternativas existen, incluida la opción de no seguir este tratamiento? ¿Qué seguimiento, monitoreo y atención de apoyo se proporcionarán? El proceso de REVIVE incluye seguimiento para monitorear el progreso y ajustar las estrategias de apoyo cuando sea médicamente apropiado.",
      },
      {
        q: "¿Cómo puedo solicitar una cita?",
        a: "Puede solicitar una consulta presencial o virtual a través del formulario Agendar Consulta de este sitio web, enviar un mensaje desde la página de Contacto, o comunicarse directamente con nuestro equipo de atención al paciente por WhatsApp al +1 (555) 303-9140. Enviar una solicitud no garantiza una cita; la cita se considera programada únicamente tras la confirmación de REVIVE. La clínica se ubica en Central Torreo, Av. Santa María, Calete, Int. 402, Tijuana, Baja California, México, y atiende de lunes a sábado, de 9:00 AM a 5:00 PM.",
      },
    ],
  },
};

const ALTERNATES = {
  en: "https://revivestem.com.mx/faq",
  es: "https://revivestem.com.mx/es/preguntas-frecuentes",
  "x-default": "https://revivestem.com.mx/faq",
};

const buildSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

const Faq = ({ es = false }) => {
  const c = es ? CONTENT.es : CONTENT.en;

  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "faq-jsonld";
    el.textContent = JSON.stringify(buildSchema(c.faqs));
    document.head.appendChild(el);
    return () => { document.getElementById("faq-jsonld")?.remove(); };
  }, [c]);

  return (
      <LegalLayout
        testid={es ? "preguntas-page" : "faq-page"}
        title={c.title}
        lastUpdated={c.lastUpdated}
        intro={c.intro}
        sections={c.faqs.map((f) => ({ h: f.q, p: [f.a] }))}
        description={c.description}
        es={es}
        altPath={c.altPath}
        seoPath={c.seoPath}
        alternates={ALTERNATES}
      />
  );
};

export default Faq;
